<template>
    <BaseSidebar negatePaddingTop :value="value" @input="toggleSidebar">
        <div
            class="d-flex flex-column align-items-stretch text-center flex-grow"
            style="max-width: 100%; justify-content: space-evenly"
        >
            <div slot="top" class="p-2" style="height: 57px">
                <LocaleDropdown
                    ref="localeDropdown"
                    noModal
                    @open="toggleLocaleList"
                    @close="toggleLocaleList"
                />
            </div>
            <b-collapse id="sidebarLocaleList" :visible="showLocaleList">
                <div class="sidebar-locale-list mb-5">
                    <div v-for="l in locales" :key="l.locale" @click="onChangeLocale(l)">
                        <div class="sidebar-locale-item clickable d-flex align-items-center py-2">
                            <FlagIcon
                                class="mr-3"
                                :country="l.countryCode"
                                size="big"
                                avatar
                                avatarSize="small"
                            />
                            <h5 class="mb-0">
                                {{ $t(`components.builders.localeDropdown.languages.${l.locale}`) }}
                            </h5>
                        </div>
                        <Divider />
                    </div>
                </div>
            </b-collapse>

            <template>
                <div class="top-section">
                    <div v-if="isAuthenticated && showProfileBanner" class="top-section-userinfo">
                        <div class="w-100 d-flex justify-content-center align-items-center">
                            <Avatar class="top-section-avatar" size="none" :image="profileImage">{{
                                firstLetterInName
                            }}</Avatar>
                        </div>
                        <div class="py-3">
                            <h6 class="caption mute">{{ email }}</h6>
                        </div>
                    </div>
                    <div v-else class="h-100">
                        <div class="image-banner header-background" alt="banner" />
                    </div>
                </div>
            </template>
            <Divider />

            <b-collapse id="sidebar-action-list" :visible="!showLocaleList">
                <div v-for="action in actions" :key="action.label">
                    <div
                        v-if="!action.show || action.show()"
                        class="clickable py-2 px-2 mt-2 d-flex align-items-center"
                        @click="pushPage(action.url, action.external, action.newTab)"
                    >
                        <BaseIcon v-if="action.icon" :name="action.icon" />
                        <img
                            v-else-if="action.svgIcon"
                            :src="action.svgIcon"
                            style="width: 20px; height: 16px; margin-top: -1px;"
                        />
                        <div v-else style="width: 20px;" />

                        <h5 class="inherit-color link text-left mb-0 ml-2">
                            {{ $t(`components.navigation.sidebar.${action.value}`) }}
                        </h5>
                    </div>
                </div>
            </b-collapse>

            <div class="flex-grow" />

            <div
                v-if="hasLogin"
                class="clickable py-2 px-2 my-2 d-flex align-items-center"
                @click="isAuthenticated ? authService.logOut() : signIn()"
            >
                <BaseIcon :name="isAuthenticated ? 'sign-out-alt' : 'sign-in-alt'" />
                <h5 class="inherit-color link text-left mb-0 ml-2">
                    {{
                        isAuthenticated
                            ? $t('components.navigation.sidebar.logOut')
                            : $t('components.navigation.sidebar.signIn')
                    }}
                </h5>
            </div>
        </div>
    </BaseSidebar>
</template>

<script>
import AuthService from '~/services/AuthService';
import LocaleDropdown from './LocaleDropdown';
import {
    SHOW_JOBS,
    SHOW_COMPANIES,
    HAS_LOGIN,
    HAS_MASTERCLASSES,
    SHOW_PROFILE_BANNER,
    HAS_ASSESSMENTS,
    HAS_COURSERA,
    HAS_MY_PROFILE,
    HAS_INTERNAL_RESOURCES,
} from '~/router';
import { getSubDomain } from '~/utils/urlUtils';

import localization from '~/localization';
import CourseraC from '~/assets/img/coursera-c.svg';

// Data
import locales from '~/data/locales.json';

export default {
    name: 'NavigationSidebar',
    components: { LocaleDropdown },
    props: {
        value: Boolean, // Show sidebar
    },

    data: function() {
        return {
            locales: locales,
            authService: AuthService,
            showLocaleList: false,
            hasLogin: HAS_LOGIN,
            showProfileBanner: SHOW_PROFILE_BANNER,
            actions: [
                /* {
                    value: 'myPage',
                    icon: 'user',
                    url: this.$URLS.mypage,
                    show: () => HAS_MY_PAGE,
                }, */
                {
                    value: 'myProfile',
                    icon: 'user',
                    url: this.$URLS.spotlight,
                    show: () => HAS_MY_PROFILE,
                },
                {
                    value: 'virtualFairs',
                    icon: 'globe-americas',
                    url: '/',
                    show: () => localization.isCompanyPlatform,
                },
                {
                    value: 'findJobs',
                    icon: 'briefcase',
                    url: this.$URLS.findjobs,
                    show: () => SHOW_JOBS,
                },
                {
                    value: 'findCompanies',
                    icon: 'building',
                    url: this.$URLS.findcompanies,
                    show: () => SHOW_COMPANIES,
                },
                {
                    value: 'virtualFairs',
                    icon: 'globe-americas',
                    url: this.$URLS.vfairs,
                    show: () => this.$interfaces.vfairModule.getHasVFairs(),
                },
                {
                    value: 'resources',
                    icon: 'video',
                    url: this.$URLS.resources,
                    show: () => HAS_MASTERCLASSES,
                },
                {
                    value: 'assessments',
                    icon: 'list-alt',
                    url: this.$URLS.assessments.concat(this.$URLS.assessmentCenter),
                    show: () => HAS_ASSESSMENTS,
                },
                {
                    value: 'coursera',
                    svgIcon: CourseraC,
                    url: this.$URLS.coursera,
                    show: () => HAS_COURSERA,
                },
                {
                    value: 'tools',
                    icon: ['fal', 'file'],
                    url: this.$URLS.internalResources,
                    show: () => HAS_INTERNAL_RESOURCES,
                },
                {
                    value: 'blog',
                    icon: ['fal', 'blog'],
                    url: 'https://highered.global/blog/',
                    external: true,
                    newTab: true,
                },
            ],
        };
    },
    computed: {
        profile() {
            return this.$store.getters['profileModule/getUser']();
        },
        name() {
            return this.profile.firstName && this.profile.lastName
                ? (this.profile.firstName + ' ' || '') + (this.profile.lastName || '')
                : '';
        },
        email() {
            return this.profile.email || '';
        },
        profileImage() {
            return this.profile.avatar;
        },
        firstLetterInName() {
            return this.name.length > 0 ? this.name[0] : '';
        },
        isAuthenticated() {
            return this.$interfaces.logInModule.isLoggedIn();
        },
    },
    methods: {
        toggleSidebar(value) {
            this.$emit('input', value);
        },
        pushPage(page, external, newTab) {
            if (external) {
                newTab ? window.open(page) : window.open(page, '_self');
            } else {
                this.$router.push(page);
            }

            this.toggleSidebar(false);
        },
        data() {
            return (this.profile || {}).data;
        },
        loading() {
            return this.profile.loading;
        },
        error() {
            return this.profile.error;
        },
        signIn() {
            this.$interfaces.logInModule.resetDisabledPopup();
            this.$interfaces.logInModule.toggleOpen();
            this.toggleSidebar();
        },
        toggleLocaleList() {
            this.showLocaleList = !this.showLocaleList;
        },
        onChangeLocale(localeItem) {
            if (this.$refs.localeDropdown) {
                this.$refs.localeDropdown.onChange(localeItem);
            }
        },

        // VFAIR HACK
        isVFairHackEnabled() {
            const config = this.$interfaces.vfairModule.getVFairHackConfig();
            if (!config) {
                return false;
            }
            const isOnExternalVFair =
                config.pavillions.external.externalPlatformSubdomain === getSubDomain();
            const isComingFromHostVFair = Boolean(config.redirectUrl);
            return (isOnExternalVFair || true) && isComingFromHostVFair;
        },
        goToGlobalVFair() {
            const config = this.$interfaces.vfairModule.getVFairHackConfig();
            if (!config) {
                return;
            }

            const redirectURL = config.redirectUrl;
            if (redirectURL) {
                window.location.href = redirectURL;
            }
        },
        // END VFAIR HACK
    },
};
</script>

<style scoped lang="scss">
.no_wrap_elipsis {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

.link {
    font-weight: 500 !important;
}

.top-section {
    width: 100%;
    background-color: white;

    height: 155px;
    position: relative;
    border-top: 3px solid var(--primary);
}

.top-section-userinfo {
    width: 100%;
    padding: 0 10px;
    position: absolute;
    top: -28px;
    height: 56px;
}

.top-section-avatar {
    box-sizing: border-box;
    height: 124px !important;
    width: 124px !important;
    border: 3px solid var(--primary);
    border-radius: 50%;
}

.image-banner {
    width: 100%;
    max-height: 100%;
    object-fit: cover;
    box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.2);

    height: 400px;
    object-fit: scale-down;
    background-size: cover;
    background-position: center;
}

.sidebar-locale-list {
    border-top: 3px solid var(--primary);
}

.sidebar-locale-item {
    padding-left: 12px;
    padding-right: 12px;
}

@media only screen and (max-height: 450px) {
    .hide-on-low-height {
        display: none;
    }
}

// VFAIR HACK
.vfairhack-root {
    border-radius: 12px;
    max-height: 40px;
    overflow: hidden;
    cursor: pointer;
    margin-right: 12px;

    width: min-content;
    margin: auto;

    img {
        max-height: 60px;
        max-width: 140px;
        object-fit: cover;
    }
}

// END VFAIR HACK
</style>
